import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePlatformAdmin } from '@/contexts/PlatformAdminContext';
import { useTenantSelection } from '@/contexts/TenantSelectionContext';
import { useUserImpersonation } from '@/contexts/UserImpersonationContext';
import { UserImpersonationDropdown } from './UserImpersonationDropdown';

const BAR_HEIGHT_VAR = '--impersonation-bar-height';

/**
 * Fixed strip shown on every `/platform-admin/tenant/:tenantId/*` screen while a platform
 * admin impersonates a tenant user. Mounted once above the routes, so screens that do not
 * use TenantManagementLayout (the flow builder, conversation detail) show it too.
 */
export function ImpersonationBar() {
  const { t } = useTranslation('platformAdmin');
  const match = useMatch('/platform-admin/tenant/:tenantId/*');
  const { isPlatformAdmin } = usePlatformAdmin();
  const { impersonatedUser, stopImpersonation, loading } = useUserImpersonation();
  const { selectedTenant, availableTenants } = useTenantSelection();

  // The fixed sidebar reads this variable to start below the strip instead of under it.
  const observer = useRef<ResizeObserver | null>(null);
  const measureRef = useCallback((node: HTMLDivElement | null) => {
    const root = document.documentElement;
    observer.current?.disconnect();
    observer.current = null;
    if (!node) {
      root.style.removeProperty(BAR_HEIGHT_VAR);
      return;
    }
    const publish = () => root.style.setProperty(BAR_HEIGHT_VAR, `${node.offsetHeight}px`);
    publish();
    observer.current = new ResizeObserver(publish);
    observer.current.observe(node);
  }, []);

  const urlTenantId = match?.params.tenantId;
  if (!urlTenantId || !isPlatformAdmin || !impersonatedUser) return null;

  const tenantName =
    availableTenants.find((tenant) => tenant.id === impersonatedUser.tenant_id)?.business_name ??
    impersonatedUser.tenant_id;
  const userName = impersonatedUser.full_name || impersonatedUser.email;

  return (
    <div
      ref={measureRef}
      role="status"
      className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-2 border-b border-warning/40 bg-warning-subtle px-4 py-2 text-foreground"
    >
      <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
        <UserCheck className="h-4 w-4 shrink-0" />
        <span className="truncate">{t('impersonation.operatingAs', { user: userName, tenant: tenantName })}</span>
      </span>
      <div className="flex min-w-0 max-w-full items-center gap-2">
        {selectedTenant?.id === urlTenantId && <UserImpersonationDropdown />}
        <Button size="sm" variant="outline" disabled={loading} onClick={() => stopImpersonation().catch(() => undefined)}>
          {t('impersonation.exit')}
        </Button>
      </div>
    </div>
  );
}
